/**
 * Turns the workspace into a model estimate. Pure apart from the bundled data
 * files (the model and the prime rate), which are build-time constants.
 */

import type { AgeBand, Goal, Workspace } from '../workspace/schema.js';
import type { RiskTable, ScoreInput, ScoreResult, UseOfFunds } from './types.js';
import { score } from './score.js';
import { PROGRAM_MAX_LOAN } from './rates.js';
import modelJson from '../data/model.json';
import primeJson from '../data/prime.json';

export const MODEL = modelJson as unknown as RiskTable;
export const PRIME = primeJson as { pct: number; asOf: string; source: string; citation: string };

/** Representative years for each answer band (only the SBA category matters). */
const YEARS: Record<AgeBand, number> = {
  'not-operating': 0, 'under-1': 0.5, '1-2': 1.5, '2-5': 3.5, 'over-5': 8,
};

const USE: Record<Goal, UseOfFunds> = {
  'working-capital': 'working-capital',
  'equipment-expansion': 'equipment',
  'buy-business': 'business-acquisition',
  'real-estate': 'real-estate',
  refinance: 'debt-refinancing',
  'not-sure': 'working-capital',
};

export type EstimateState =
  | { ready: true; input: ScoreInput; result: ScoreResult }
  | { ready: false; missing: string[] };

export function scoreInputFrom(ws: Workspace): { input?: ScoreInput; missing: string[] } {
  const p = ws.profile;
  const missing: string[] = [];
  if (!p.amount || p.amount.value <= 0) missing.push('how much you need');
  if (!p.industry) missing.push('your industry');
  if (!p.ageBand && !p.stage) missing.push('how long the business has operated');
  if (missing.length) return { missing };

  const goal = p.goal?.value ?? 'not-sure';
  const input: ScoreInput = {
    amount: p.amount!.value,
    termMonths: p.termMonths?.value ?? 120,
    naics: p.industry!.value,
    yearsInOperation: YEARS[p.ageBand?.value ?? (p.stage?.value === 'planning' ? 'not-operating' : 'over-5')],
    isChangeOfOwnership: goal === 'buy-business' || p.stage?.value === 'acquiring',
    useOfFunds: USE[goal],
    ...(p.jobs ? { jobsSupported: p.jobs.value } : {}),
    ...(p.annualRevenue ? { annualRevenue: p.annualRevenue.value } : {}),
    ...(p.existingAnnualDebtService ? { existingAnnualDebtService: p.existingAnnualDebtService.value } : {}),
  };
  return { input, missing };
}

export function estimate(ws: Workspace): EstimateState {
  const { input, missing } = scoreInputFrom(ws);
  if (!input) return { ready: false, missing };
  const result = score(input, MODEL, { basePct: PRIME.pct, baseAsOf: PRIME.asOf, baseIsFallback: false });
  return { ready: true, input, result };
}

/**
 * "About X in 100" — one formatter for every screen and the action plan, so the
 * same estimate never reads as 2.8 in one place and 3 in another.
 */
export function formatPer100(p: number): string {
  const v = p * 100;
  if (v < 10) return v.toFixed(1).replace(/\.0$/, '');
  return String(Math.round(v));
}

/** A starting rate for scenarios: the estimate's middle, else prime plus the median observed spread. */
export function defaultRatePct(ws: Workspace): number {
  const e = estimate(ws);
  if (e.ready) return e.result.rate.estimateLikelyPct;
  return Math.round((PRIME.pct + MODEL.rateSpreads.global.p50) * 100) / 100;
}

export { PROGRAM_MAX_LOAN };
